import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { initialState } from '@/redux/initialState/chat'
import { ChatMessage } from '@/redux/types/chat'
import { SenderType } from '@/constants/chat'
import {
  ResourceUsage,
  ResourceRemaining,
  ResourceLimits,
} from '@/redux/types/resourceLimits'

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage(state, action: PayloadAction<ChatMessage>) {
      const existingMessageIndex = state.activeChatMessages.findIndex(
        (msg) => msg.id === action.payload.id
      )

      if (existingMessageIndex !== -1) {
        state.activeChatMessages[existingMessageIndex] = action.payload
      } else {
        state.activeChatMessages.push(action.payload)
      }
    },
    setupAdvisorMessages(
      state,
      action: PayloadAction<{
        messages: ChatMessage[]
        hasMore?: boolean
        totalCount?: number
        currentPage?: number
      }>
    ) {
      const { messages, hasMore, totalCount, currentPage } = action.payload
      state.advisorChatMessages = messages
      state.advisorChatHasMore = hasMore || false
      state.advisorChatTotalCount = totalCount || 0
      state.advisorChatCurrentPage = currentPage || 1
    },

    updateAdvisorMessage(state, action: PayloadAction<ChatMessage>) {
      const messageIndex = state.advisorChatMessages.findIndex(
        (msg) => msg.id === action.payload.id
      )

      if (messageIndex !== -1) {
        const existing = state.advisorChatMessages[messageIndex]
        if (action.payload.message) {
          existing.message += action.payload.message
        }
        if (action.payload.streaming === false) {
          existing.streaming = false
        } else if (action.payload.streaming === true) {
          existing.streaming = true
        }
        if (action.payload.created_at !== undefined) {
          existing.created_at = action.payload.created_at
        }
        if (action.payload.llm_provider !== undefined) {
          existing.llm_provider = action.payload.llm_provider
        }
        if (action.payload.llm_model_name !== undefined) {
          existing.llm_model_name = action.payload.llm_model_name
        }
      } else {
        // Add the new message
        state.advisorChatMessages.push(action.payload)
      }
    },

    updateNegotiationMessage(state, action: PayloadAction<ChatMessage>) {
      state.negotiationChatMessages.push(action.payload)
    },

    setInput(state, action: PayloadAction<string>) {
      state.input = action.payload
    },
    setNegotiationDraft(
      state,
      action: PayloadAction<{ negotiationId: string; draft: string }>
    ) {
      state.negotiationDrafts[action.payload.negotiationId] =
        action.payload.draft
    },
    setAdvisorDraft(state, action: PayloadAction<string>) {
      state.advisorDraft = action.payload
    },
    clearAllNegotiationDrafts(state) {
      state.negotiationDrafts = {}
    },
    setActiveAdvisor(state, action: PayloadAction<string[]>) {
      state.activeAdvisor = action.payload
    },

    setActiveProfessor(state, action: PayloadAction<boolean>) {
      state.activeProfessor = action.payload
    },
    setIsGenerating(state, action: PayloadAction<boolean>) {
      state.isGenerating = action.payload
    },

    setActiveTab(state, action) {
      state.activeTab = action.payload
    },

    clearMessages(state) {
      state.activeChatMessages = []
      state.advisorChatMessages = []
      state.negotiationChatMessages = []
      state.input = ''
      state.activeAdvisor = []
      state.activeProfessor = false
      state.advisorUnreadCount = 0
    },
    incrementAdvisorUnread(state) {
      state.advisorUnreadCount += 1
    },
    clearAdvisorUnread(state) {
      state.advisorUnreadCount = 0
    },
    setLoadingAdvisorMsgs: (state, action: PayloadAction<boolean>) => {
      state.loadingAdvisorMsgs = action.payload
    },
    stopAllStreamingMessages: (state) => {
      state.advisorChatMessages = state.advisorChatMessages.map((message) => {
        if (message.streaming && message.sender_type === SenderType.Chatbot) {
          return {
            ...message,
            streaming: false,
          }
        }
        return message
      })
    },
    resourceLimitsUpdate: (
      state,
      action: PayloadAction<{
        usage?: Partial<ResourceUsage>
        remaining?: Partial<ResourceRemaining>
        limits?: Partial<ResourceLimits>
      }>
    ) => {
      const currentUsage = state.resourceLimits?.usage
      const currentRemaining = state.resourceLimits?.remaining

      state.resourceLimits = {
        ...state.resourceLimits,
        message_limit:
          action.payload.limits?.message_limit ??
          state.resourceLimits?.message_limit,
        token_limit:
          action.payload.limits?.token_limit ??
          state.resourceLimits?.token_limit,
        currency_limit:
          action.payload.limits?.currency_limit ??
          state.resourceLimits?.currency_limit,
        usage: action.payload.usage
          ? {
              messages_used:
                action.payload.usage.messages_used ??
                currentUsage?.messages_used ??
                0,
              tokens_used:
                action.payload.usage.tokens_used ??
                currentUsage?.tokens_used ??
                0,
              currency_spent:
                action.payload.usage.currency_spent ??
                currentUsage?.currency_spent ??
                0,
            }
          : currentUsage,
        remaining: action.payload.remaining
          ? {
              messages_remaining:
                action.payload.remaining.messages_remaining ??
                currentRemaining?.messages_remaining ??
                null,
              tokens_remaining:
                action.payload.remaining.tokens_remaining ??
                currentRemaining?.tokens_remaining ??
                null,
              currency_remaining:
                action.payload.remaining.currency_remaining ??
                currentRemaining?.currency_remaining ??
                null,
            }
          : currentRemaining,
      }
    },
    loadMoreAdvisorMessages(
      state,
      action: PayloadAction<{
        messages: any[]
        hasMore?: boolean
        totalCount?: number
        currentPage?: number
      }>
    ) {
      const { messages, hasMore, totalCount, currentPage } = action.payload
      state.advisorChatMessages = [...messages, ...state.advisorChatMessages]
      state.advisorChatHasMore = hasMore || false
      state.advisorChatTotalCount = totalCount || 0
      state.advisorChatCurrentPage = currentPage || 1
    },

    setAdvisorChatLoadingMore(state, action: PayloadAction<boolean>) {
      state.advisorChatLoadingMore = action.payload
    },
    setResourceLimits: (
      state,
      action: PayloadAction<{
        resource_limits_enabled: boolean
        resource_enforcement_mode?: string
        message_limit?: number | null
        token_limit?: number | null
        currency_limit?: number | null
        usage?: ResourceUsage
        remaining?: ResourceRemaining
      }>
    ) => {
      state.resourceLimits = action.payload
    },
  },
  extraReducers: () => {},
})

export const {
  updateAdvisorMessage,
  updateNegotiationMessage,
  addMessage,
  setupAdvisorMessages,
  setActiveAdvisor,
  setActiveProfessor,
  setInput,
  setActiveTab,
  clearMessages,
  setIsGenerating,
  setLoadingAdvisorMsgs,
  setAdvisorDraft,
  setNegotiationDraft,
  clearAllNegotiationDrafts,
  stopAllStreamingMessages,
  loadMoreAdvisorMessages,
  setAdvisorChatLoadingMore,
  resourceLimitsUpdate,
  setResourceLimits,
  incrementAdvisorUnread,
  clearAdvisorUnread,
} = chatSlice.actions
export default chatSlice.reducer
